import React, { useState } from 'react';

import Button from '@sats-group/ui-lib/react/button/button';
import HiddenInput from '@sats-group/ui-lib/react/hidden-input/hidden-input';
import LinkButton from '@sats-group/ui-lib/react/link-button/link-button';
import LogoSats from '@sats-group/ui-lib/react/logos/sats';
import Message from '@sats-group/ui-lib/react/message/message';
import Text from '@sats-group/ui-lib/react/text';
import TextInput from '@sats-group/ui-lib/react/text-input/text-input';

import ContentContainer from 'shared/components/content-container/content-container';
import Spinner from 'shared/components/spinner/spinner';
import { useMessage } from 'shared/hooks/use-message';
import type { NamedFC } from 'shared/types/named-fc.types';

import type { Login as Props } from './login.props';

const Login: NamedFC<Props> = ({
  description,
  endpoint,
  hiddenInputs = [],
  language,
  messages,
  password,
  satsGroupAuth,
  submit,
  title,
  username,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { messagesQueue, getAction } = useMessage(messages, language);

  const handleSubmit = (e: React.ChangeEvent<HTMLFormElement>) => {
    if (isLoading) {
      e.preventDefault();
    }

    setIsLoading(true);
  };

  return (
    <div className="login-page">
      <ContentContainer theme={ContentContainer.themes.narrow}>
        <div className="login-page__card">
          <div className="login-page__logo">
            <LogoSats />
          </div>
          <Text
            className="login-page__info"
            elementName="h1"
            size={Text.sizes.headline3}
          >
            {title}
          </Text>
          <div className="login-page__info">
            <Text>{description}</Text>
          </div>
          {messagesQueue.length ? (
            <div className="login-page__messages">
              {messagesQueue.map(message => (
                <Message
                  key={message.text}
                  {...message}
                  action={getAction(message)}
                />
              ))}
            </div>
          ) : null}
          <form action={endpoint} method="post" onSubmit={handleSubmit}>
            {isLoading ? <Spinner theme={Spinner.themes.overlay} /> : null}
            {hiddenInputs.map(input => (
              <HiddenInput {...input} key={input.name} />
            ))}
            <div className="login-page__inputs">
              <div key="user">
                <TextInput {...username} />
              </div>
              <div key="password">
                <TextInput {...password} />
              </div>
            </div>
            <div className="login-page__actions">
              <Button {...submit} size={Button.sizes.large} />
            </div>
          </form>
        </div>
        {satsGroupAuth ? (
          <div className="login-page__sats-group-auth">
            <LinkButton {...satsGroupAuth} data-test-sats-group-login />
          </div>
        ) : null}
      </ContentContainer>
    </div>
  );
};

Login.displayName = 'Login';

export default Login;
